import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Trash2, LayoutGrid, Rows3, ChevronDown, X, Check, Workflow, Bug, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import AgentTaskTable from "../components/AgentTaskTable";
import ProductTaskTable from "../components/ProductTaskTable";
import PeopleView from "../components/PeopleView";

const STATUS_OPTIONS = [
  { value: "new",         label: "New",         bg: "bg-sky-100",    text: "text-sky-700" },
  { value: "in_progress", label: "In Progress", bg: "bg-butter-100", text: "text-amber-700" },
  { value: "done",        label: "Done",        bg: "bg-mint-100",   text: "text-emerald-700" },
  { value: "stuck",       label: "Stuck",       bg: "bg-rose-100",   text: "text-rose-700" },
  { value: "planning",    label: "Planning",    bg: "bg-lilac-100",  text: "text-purple-700" },
];

const PRIORITY_OPTIONS = [
  { value: "low",      label: "Low",      bg: "bg-slate-100",  text: "text-slate-700" },
  { value: "medium",   label: "Medium",   bg: "bg-sky-100",    text: "text-sky-700" },
  { value: "high",     label: "High",     bg: "bg-peach-100",  text: "text-orange-700" },
  { value: "critical", label: "Critical", bg: "bg-rose-100",   text: "text-rose-700" },
];

const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];
const priorityMeta = (v) => PRIORITY_OPTIONS.find((p) => p.value === v) || PRIORITY_OPTIONS[1];

// Stable color per section label
const SECTION_PALETTE = [
  { bg: "bg-sky-100",    text: "text-sky-800",    border: "border-sky-200" },
  { bg: "bg-mint-100",   text: "text-emerald-800", border: "border-mint-200" },
  { bg: "bg-butter-100", text: "text-amber-800",   border: "border-butter-200" },
  { bg: "bg-peach-100",  text: "text-amber-900",   border: "border-peach-200" },
  { bg: "bg-lilac-100",  text: "text-purple-800",  border: "border-lilac-200" },
  { bg: "bg-rose-100",   text: "text-rose-800",    border: "border-rose-200" },
];
function sectionMeta(label) {
  if (!label) return SECTION_PALETTE[0];
  let h = 0;
  for (let i = 0; i < label.length; i += 1) h = (h * 31 + label.charCodeAt(i)) | 0;
  return SECTION_PALETTE[Math.abs(h) % SECTION_PALETTE.length];
}

function SectionMultiSelect({ value = [], options = [], onChange, onOptionsChange, testid }) {
  const [open, setOpen] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [draft, setDraft] = useState("");

  const toggle = (label) => {
    const has = value.includes(label);
    onChange(has ? value.filter((x) => x !== label) : [...value, label]);
  };

  const addNew = () => {
    const v = draft.trim();
    if (!v) return;
    if (!options.includes(v)) onOptionsChange([...options, v]);
    onChange(value.includes(v) ? value : [...value, v]);
    setDraft("");
    setAddingNew(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full min-h-[32px] h-auto px-2 py-1 rounded-md text-xs text-left flex flex-wrap items-center gap-1 bg-transparent hover:bg-slate-100 focus:bg-white focus:border focus:border-slate-200 focus:outline-none transition-colors"
        >
          {value.length === 0 ? (
            <span className="text-slate-400">—</span>
          ) : (
            value.map((v) => {
              const m = sectionMeta(v);
              return (
                <span key={v} className={`h-6 px-2 rounded-md text-xs font-medium inline-flex items-center border ${m.bg} ${m.text} ${m.border}`}>
                  {v}
                </span>
              );
            })
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">Sections</p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {options.length === 0 && (
            <p className="text-xs text-slate-400">No sections yet — add one below.</p>
          )}
          {options.map((opt) => {
            const checked = value.includes(opt);
            const m = sectionMeta(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className="w-full flex items-center gap-2 h-8 px-2 rounded-md hover:bg-slate-50 transition-colors group"
                data-testid={`task-section-opt-${opt}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300"}`}>
                  {checked && <Check className="h-3 w-3" />}
                </span>
                <span className={`flex-1 text-left h-6 px-2 rounded text-xs font-medium ${m.bg} ${m.text} inline-flex items-center`}>
                  {opt}
                </span>
                <span
                  role="button"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOptionsChange(options.filter((x) => x !== opt));
                    if (value.includes(opt)) onChange(value.filter((x) => x !== opt));
                  }}
                  className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"
                  title={`Remove "${opt}"`}
                >
                  <Trash2 className="h-3 w-3" />
                </span>
              </button>
            );
          })}
        </div>
        <div className="h-px bg-slate-100 my-3" />
        {addingNew ? (
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); addNew(); }
                if (e.key === "Escape") { setDraft(""); setAddingNew(false); }
              }}
              placeholder="New section name"
              className="h-8 text-sm"
              data-testid="task-section-new-input"
            />
            <Button size="sm" onClick={addNew} className="btn-brand h-8">Add</Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddingNew(true)}
            className="w-full h-8 rounded-md border border-dashed border-slate-200 text-xs font-medium text-slate-500 hover:text-slate-800 hover:border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-1 transition-all"
            data-testid="task-section-add-option"
          >
            <Plus className="h-3.5 w-3.5" /> Add section
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StatusPill({ value, onChange, testid }) {
  const m = statusMeta(value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className={`h-8 border-none rounded-md ${m.bg} ${m.text} font-medium text-xs w-full justify-between px-2 hover:brightness-95 focus:ring-0 focus:ring-offset-0`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${o.bg}`} />
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PriorityPill({ value, onChange, testid }) {
  const m = priorityMeta(value);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className={`h-8 border-none rounded-md ${m.bg} ${m.text} font-medium text-xs w-full justify-between px-2 hover:brightness-95 focus:ring-0 focus:ring-offset-0`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PRIORITY_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AssigneeSelect({ value, onChange, members, testid, className = "" }) {
  // value is email (or "" for unassigned)
  const SENTINEL = "__unassigned__";
  return (
    <Select value={value ? value : SENTINEL} onValueChange={(v) => onChange(v === SENTINEL ? "" : v)}>
      <SelectTrigger
        data-testid={testid}
        className={`h-8 border-none bg-transparent text-sm text-slate-700 hover:bg-slate-100 focus:ring-0 focus:ring-offset-0 focus:bg-white focus:border focus:border-slate-200 rounded-md justify-between px-2 ${className}`}
      >
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SENTINEL}>
          <span className="text-slate-500">Unassigned</span>
        </SelectItem>
        {members.length === 0 ? (
          <div className="px-2 py-2 text-xs text-slate-400">Add members in Users →</div>
        ) : (
          members.map((m) => (
            <SelectItem key={m.id} value={m.email}>
              <span className="flex items-center gap-2">
                <span className="h-5 w-5 rounded-full bg-lilac-100 text-purple-700 text-[10px] font-semibold flex items-center justify-center">
                  {(m.name || m.email).slice(0, 1).toUpperCase()}
                </span>
                <span>{m.name}</span>
                <span className="text-[11px] text-slate-400">{m.email}</span>
              </span>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

function NewTaskDialog({ open, onOpenChange, groups, defaultGroupId, members, onCreate }) {
  const [title, setTitle] = useState("");
  const [groupId, setGroupId] = useState(defaultGroupId || "");
  const [status, setStatus] = useState("new");
  const [priority, setPriority] = useState("medium");
  const [assignee, setAssignee] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setGroupId(defaultGroupId || groups[0]?.id || "");
      setStatus("new");
      setPriority("medium");
      setAssignee("");
      setDueDate("");
      setNotes("");
    }
  }, [open, defaultGroupId, groups]);

  const submit = async () => {
    if (!title.trim() || !groupId) {
      toast.error("Title and group are required");
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({ title, group_id: groupId, status, priority, assignee, due_date: dueDate || null, notes });
      onOpenChange(false);
      toast.success("Task created");
    } catch (e) {
      toast.error("Failed to create task");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Describe the task…"
              className="mt-1"
              data-testid="new-task-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Group</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger className="mt-1" data-testid="new-task-group">
                  <SelectValue placeholder="Select group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Assignee</Label>
            <Select value={assignee || "__unassigned__"} onValueChange={(v) => setAssignee(v === "__unassigned__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="new-task-assignee">
                <SelectValue placeholder="Select a team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">
                  <span className="text-slate-500">Unassigned</span>
                </SelectItem>
                {members.length === 0 ? (
                  <div className="px-2 py-2 text-xs text-slate-400">Add members in Users first</div>
                ) : (
                  members.map((m) => (
                    <SelectItem key={m.id} value={m.email}>
                      <span className="flex items-center gap-2">
                        <span className="h-5 w-5 rounded-full bg-lilac-100 text-purple-700 text-[10px] font-semibold flex items-center justify-center">
                          {(m.name || m.email).slice(0, 1).toUpperCase()}
                        </span>
                        <span>{m.name}</span>
                        <span className="text-[11px] text-slate-400">{m.email}</span>
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-task-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="new-task-submit">
            {submitting ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewGroupDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setName(""); }, [open]);
  const submit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate({ name });
      onOpenChange(false);
      toast.success("Group created");
    } catch {
      toast.error("Failed to create group");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="new-group-dialog">
        <DialogHeader><DialogTitle className="font-display">New group</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" data-testid="new-group-name" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="new-group-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TableView({ groups, tasksByGroup, members, sectionOptions, onUpdateTask, onUpdateSectionOptions, onDeleteTask, onAddTaskInGroup }) {
  return (
    <div className="space-y-6">
      {groups.map((g) => {
        const items = tasksByGroup[g.id] || [];
        return (
          <div key={g.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`group-${g.id}`}>
            {/* Group header */}
            <div className="h-12 bg-slate-50 px-4 flex items-center gap-3 border-b border-slate-200">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color }} />
              <span className="font-medium text-slate-800">{g.name}</span>
              <span className="text-xs text-slate-500">{items.length} {items.length === 1 ? "task" : "tasks"}</span>
              <button
                onClick={() => onAddTaskInGroup(g.id)}
                data-testid={`add-task-${g.id}`}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900"
              >
                <Plus className="h-3.5 w-3.5" /> Add task
              </button>
            </div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_140px_140px_140px_1.2fr_140px_40px] text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 px-4 h-9 items-center border-b border-slate-100">
              <div>Task</div>
              <div>Status</div>
              <div>Priority</div>
              <div>Assignee</div>
              <div>Sections</div>
              <div>Due</div>
              <div></div>
            </div>
            {/* Rows */}
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-slate-400">No tasks in this group yet</div>
            ) : (
              items.map((t) => (
                <div
                  key={t.id}
                  className="grid grid-cols-[1fr_140px_140px_140px_1.2fr_140px_40px] items-start gap-2 px-4 min-h-[56px] py-2 border-b border-slate-100 hover:bg-slate-50/60 transition-colors group"
                  data-testid={`task-row-${t.id}`}
                >
                  <input
                    defaultValue={t.title}
                    onBlur={(e) => {
                      if (e.target.value !== t.title && e.target.value.trim()) {
                        onUpdateTask(t.id, { title: e.target.value });
                      } else {
                        e.target.value = t.title;
                      }
                    }}
                    className="bg-transparent text-sm text-slate-800 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 transition-all mt-0.5"
                    data-testid={`task-title-${t.id}`}
                  />
                  <div className="mt-0.5">
                    <StatusPill value={t.status} onChange={(v) => onUpdateTask(t.id, { status: v })} testid={`task-status-${t.id}`} />
                  </div>
                  <div className="mt-0.5">
                    <PriorityPill value={t.priority} onChange={(v) => onUpdateTask(t.id, { priority: v })} testid={`task-priority-${t.id}`} />
                  </div>
                  <div className="mt-0.5">
                    <AssigneeSelect
                      value={t.assignee || ""}
                      onChange={(v) => onUpdateTask(t.id, { assignee: v })}
                      members={members}
                      testid={`task-assignee-${t.id}`}
                    />
                  </div>
                  <SectionMultiSelect
                    value={t.sections || []}
                    options={sectionOptions}
                    onChange={(v) => onUpdateTask(t.id, { sections: v })}
                    onOptionsChange={onUpdateSectionOptions}
                    testid={`task-sections-${t.id}`}
                  />
                  <input
                    type="date"
                    defaultValue={t.due_date || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (t.due_date || "")) {
                        onUpdateTask(t.id, { due_date: e.target.value || null });
                      }
                    }}
                    className="bg-transparent text-sm text-slate-700 outline-none focus:bg-white focus:px-2 focus:rounded-md focus:border focus:border-slate-200 h-8 w-full transition-all mt-0.5"
                  />
                  <button
                    onClick={() => onDeleteTask(t.id)}
                    className="opacity-0 group-hover:opacity-100 h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all flex items-center justify-center mt-0.5"
                    data-testid={`task-delete-${t.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

function KanbanView({ groups, tasksByGroup, members, onUpdateTask, onAddTaskInGroup, onDeleteTask }) {
  const memberByEmail = useMemo(() => {
    const m = new Map();
    for (const x of members) m.set(x.email, x);
    return m;
  }, [members]);
  return (
    <div className="flex gap-5 overflow-x-auto pb-4" data-testid="kanban-view">
      {groups.map((g) => {
        const items = tasksByGroup[g.id] || [];
        return (
          <div key={g.id} className="w-[320px] shrink-0">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="h-12 px-4 flex items-center gap-2 border-b border-slate-100">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color }} />
                <span className="font-medium text-slate-800">{g.name}</span>
                <span className="text-xs text-slate-500">{items.length}</span>
                <button
                  onClick={() => onAddTaskInGroup(g.id)}
                  className="ml-auto h-7 w-7 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <div className="p-3 space-y-2 min-h-[200px]">
                {items.length === 0 && (
                  <div className="py-10 text-center text-xs text-slate-400">No tasks</div>
                )}
                {items.map((t) => {
                  const s = statusMeta(t.status);
                  const p = priorityMeta(t.priority);
                  return (
                    <div key={t.id} className="group rounded-xl border border-slate-200 p-3 hover:border-slate-300 bg-white transition-colors" data-testid={`kanban-card-${t.id}`}>
                      <p className="text-sm font-medium text-slate-900 leading-snug">{t.title}</p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className={`${s.bg} ${s.text} text-[11px] font-medium px-2 py-0.5 rounded`}>{s.label}</span>
                        <span className={`${p.bg} ${p.text} text-[11px] font-medium px-2 py-0.5 rounded`}>{p.label}</span>
                        {(t.sections || []).map((sec) => {
                          const m = sectionMeta(sec);
                          return (
                            <span key={sec} className={`text-[11px] font-medium px-2 py-0.5 rounded border ${m.bg} ${m.text} ${m.border}`}>{sec}</span>
                          );
                        })}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                        <span>{t.assignee ? (memberByEmail.get(t.assignee)?.name || t.assignee) : "Unassigned"}</span>
                        <button
                          onClick={() => onDeleteTask(t.id)}
                          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Bidirectional map between the internal page key and the URL slug
const PAGE_TO_SLUG = { tasks: "product", security: "security", workflow: "agent", bugs: "bugs", people: "people" };
const SLUG_TO_PAGE = { product: "tasks", security: "security", agent: "workflow", bugs: "bugs", people: "people" };

export default function Tasks() {
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [activeSection, setActiveSection] = useState("__all__");
  const [view, setView] = useState("table");
  const [page, setPage] = useState("tasks"); // "tasks" | "bugs" | "workflow" | "people"

  // --- URL <-> page tab sync ---
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();

  const selectPage = (p) => {
    setPage(p);
    navigate(`/tasks/${PAGE_TO_SLUG[p] || "product"}`);
  };

  useEffect(() => {
    if (!tabParam) { navigate("/tasks/product", { replace: true }); return; }
    const p = SLUG_TO_PAGE[tabParam.toLowerCase()];
    if (p) setPage(p);
    else navigate("/tasks/product", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState(null);
  const [agents, setAgents] = useState([]);
  const [apis, setApis] = useState([]);

  const load = async () => {
    try {
      const [g, t, m, s, a, p] = await Promise.all([
        api.get("/groups"),
        api.get("/tasks"),
        api.get("/members"),
        api.get("/settings/task-sections"),
        api.get("/agents"),
        api.get("/apis"),
      ]);
      setGroups(g.data);
      setTasks(t.data);
      setMembers(m.data);
      setSectionOptions(Array.isArray(s.data) ? s.data : []);
      setAgents(a.data);
      setApis(p.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSectionOptions = async (next) => {
    setSectionOptions(next);
    try {
      const { data } = await api.put("/settings/task-sections", { sections: next });
      setSectionOptions(data);
    } catch { toast.error("Failed to save sections"); load(); }
  };

  const filteredTasks = useMemo(() => {
    if (activeSection === "__all__") return tasks;
    return tasks.filter((t) => (t.sections || []).includes(activeSection));
  }, [tasks, activeSection]);

  const tasksByGroup = useMemo(() => {
    const m = {};
    for (const g of groups) m[g.id] = [];
    for (const t of filteredTasks) {
      if (!m[t.group_id]) m[t.group_id] = [];
      m[t.group_id].push(t);
    }
    return m;
  }, [groups, filteredTasks]);

  const handleCreate = async (payload) => {
    const { data } = await api.post("/tasks", payload);
    setTasks((prev) => [...prev, data]);
  };

  const handleUpdate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      setTasks((prev) => prev.map((t) => (t.id === id ? data : t)));
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success("Task deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleCreateGroup = async (payload) => {
    const { data } = await api.post("/groups", payload);
    setGroups((prev) => [...prev, data]);
  };

  const openNewTask = (gid) => {
    setTargetGroupId(gid || null);
    setNewTaskOpen(true);
  };

  const handleAgentUpdate = async (id, patch) => {
    const { data } = await api.patch(`/agents/${id}`, patch);
    setAgents((prev) => prev.map((a) => (a.id === id ? data : a)));
    return data;
  };

  const isAgentSection = activeSection && activeSection.toLowerCase() === "agent";

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Page toggle: Product / Security / Agent / Bugs / People */}
      <div className="inline-flex items-center bg-white border border-slate-200 rounded-2xl p-1 flex-wrap" data-testid="tasks-page-toggle">
        <button
          onClick={() => selectPage("tasks")}
          className={`h-9 px-4 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${page === "tasks" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="tasks-page-tab-tasks"
        >
          <Rows3 className="h-4 w-4" /> Product
        </button>
        <button
          onClick={() => selectPage("security")}
          className={`h-9 px-4 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${page === "security" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="tasks-page-tab-security"
        >
          <ShieldCheck className="h-4 w-4" /> Security
        </button>
        <button
          onClick={() => selectPage("workflow")}
          className={`h-9 px-4 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${page === "workflow" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="tasks-page-tab-workflow"
        >
          <Workflow className="h-4 w-4" /> Agent
        </button>
        <button
          onClick={() => selectPage("bugs")}
          className={`h-9 px-4 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${page === "bugs" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="tasks-page-tab-bugs"
        >
          <Bug className="h-4 w-4" /> Bugs
        </button>
        <button
          onClick={() => selectPage("people")}
          className={`h-9 px-4 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${page === "people" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid="tasks-page-tab-people"
        >
          <UsersIcon className="h-4 w-4" /> People
        </button>
      </div>

      {page === "workflow" ? (
        <AgentTaskTable
          agents={agents}
          apis={apis}
          members={members}
          onAgentUpdate={handleAgentUpdate}
        />
      ) : page === "bugs" ? (
        <ProductTaskTable members={members} agents={agents} kind="bug" />
      ) : page === "security" ? (
        <ProductTaskTable members={members} agents={agents} kind="security" />
      ) : page === "people" ? (
        <PeopleView members={members} agents={agents} />
      ) : (
        <ProductTaskTable members={members} agents={agents} kind="product" />
      )}

      <NewTaskDialog
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
        groups={groups}
        defaultGroupId={targetGroupId}
        members={members}
        onCreate={handleCreate}
      />
      <NewGroupDialog open={newGroupOpen} onOpenChange={setNewGroupOpen} onCreate={handleCreateGroup} />
    </div>
  );
}
